import {
	BookOpen,
	Calendar,
	Home,
	LogOut,
	Menu,
	Moon,
	Plus,
	Settings,
	ShieldCheck,
	Sun,
	Target,
	User,
	UserPlus,
	Users,
	X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/primitives";
import { useAuth } from "@/contexts/AuthContext";
import { TermsDialog } from "@/features/terms/TermsDialog";

export function Layout({ children }: { children: ReactNode }) {
	const { profile, signOut } = useAuth();
	const navigate = useNavigate();
	const [darkMode, setDarkMode] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [showTerms, setShowTerms] = useState(false);

	const handleSignOut = async () => {
		// Store user type before signing out (profile will be cleared)
		const isMentor = profile?.user_type === "mentor";
		await signOut();
		// Redirect to appropriate login page based on user type
		navigate(isMentor ? "/mentor-login" : "/login");
	};

	const toggleDarkMode = () => {
		setDarkMode(!darkMode);
		document.documentElement.classList.toggle("dark");
	};

	let navigationItems =
		profile?.user_type === "mentor"
			? [
					{ name: "Home", href: "/mentor", icon: Home },
					{ name: "Mentees", href: "/mentor/my-mentees", icon: Users },
					{ name: "Groups", href: "/mentor/my-groups", icon: UserPlus },
					{ name: "Topics", href: "/mentor/topics", icon: BookOpen },
				]
			: [
					{ name: "Home", href: "/", icon: Home },
					{ name: "Daily Check-ins", href: "/check-ins", icon: Calendar },
					{ name: "Reflections", href: "/reflections", icon: BookOpen },
					{
						name: "Actions",
						href: "/corrective-actions",
						icon: Target,
					},
				];

	if (profile?.is_admin) {
		navigationItems.push({ name: "Admin", href: "/admin", icon: ShieldCheck });
	}

	return (
		<div className="min-h-screen bg-background flex flex-col">
			{/* Navigation Header */}
			<header className="bg-background border-b">
				<div className="max-w-7xl mx-auto pr-4 sm:pr-6 lg:pr-8">
					<div className="flex justify-between items-center h-16">
						{/* Logo */}
						<div className="flex items-center">
							<Link
								to="/"
								className="flex items-center"
								data-testid="logo-link"
							>
								<img
									src="/img/Reflection Journal logo and text.png"
									alt="Reflection Journal"
									className="h-12 block dark:hidden"
								/>
								<img
									src="/img/Reflection Journal logo and text REVERSE.png"
									alt="Reflection Journal (Dark)"
									className="h-12 hidden dark:block"
								/>
							</Link>
						</div>

						{/* Navigation & User Menu - Right Side */}
						<div className="flex items-center space-x-8">
							{/* Desktop Navigation */}
							<nav className="hidden md:flex space-x-8">
								{navigationItems.map((item) => {
									const Icon = item.icon;
									return (
										<Link
											key={item.name}
											to={item.href}
											className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
											data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
										>
											<Icon className="h-4 w-4" />
											<span>{item.name}</span>
										</Link>
									);
								})}
							</nav>

							{/* User Menu & Settings */}
							<div className="flex items-center space-x-4">
								<Button
									variant="ghost"
									size="icon"
									onClick={toggleDarkMode}
									className="hidden md:flex"
									data-testid="theme-toggle-desktop"
								>
									{darkMode ? (
										<Sun className="h-4 w-4" />
									) : (
										<Moon className="h-4 w-4" />
									)}
								</Button>

								<div className="relative group z-50">
									<Button
										variant="ghost"
										size="icon"
										className="hidden md:flex"
										data-testid="user-menu-button"
									>
										<User className="h-4 w-4" />
									</Button>
									<div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
										<div className="py-1">
											<div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b">
												{profile?.name}
												<div className="text-xs text-gray-500 capitalize">
													{profile?.user_type}
												</div>
											</div>
											<button
												onClick={handleSignOut}
												className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
												data-testid="sign-out-desktop"
											>
												<LogOut className="h-4 w-4 mr-2" />
												Sign out
											</button>
										</div>
									</div>
								</div>

								{/* Mobile menu button */}
								<Button
									variant="ghost"
									size="icon"
									className="md:hidden"
									onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
									data-testid="mobile-menu-toggle"
								>
									{mobileMenuOpen ? (
										<X className="h-4 w-4" />
									) : (
										<Menu className="h-4 w-4" />
									)}
								</Button>
							</div>
						</div>
					</div>
				</div>

				{/* Mobile Navigation */}
				{mobileMenuOpen && (
					<div
						className="md:hidden bg-white dark:bg-gray-800 border-t"
						data-testid="mobile-menu"
					>
						<div className="px-2 pt-2 pb-3 space-y-1">
							{navigationItems.map((item) => {
								const Icon = item.icon;
								return (
									<Link
										key={item.name}
										to={item.href}
										className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
										onClick={() => setMobileMenuOpen(false)}
										data-testid={`nav-link-mobile-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
									>
										<Icon className="h-4 w-4" />
										<span>{item.name}</span>
									</Link>
								);
							})}
							<div className="border-t pt-2">
								<button
									onClick={toggleDarkMode}
									className="flex items-center space-x-2 w-full px-3 py-2 text-gray-600 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
									data-testid="theme-toggle-mobile"
								>
									{darkMode ? (
										<Sun className="h-4 w-4" />
									) : (
										<Moon className="h-4 w-4" />
									)}
									<span>Toggle theme</span>
								</button>
								<button
									onClick={handleSignOut}
									className="flex items-center space-x-2 w-full px-3 py-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
									data-testid="sign-out-mobile"
								>
									<LogOut className="h-4 w-4" />
									<span>Sign out</span>
								</button>
							</div>
						</div>
					</div>
				)}
			</header>

			{/* Main Content */}
			<main className="flex-1">{children}</main>

			{/* Footer */}
			<footer className="bg-background border-t">
				<div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col items-center justify-center space-y-3">
						<img
							src="/img/clarity-insights-logo.png"
							alt="Clarity Insights Logo"
							className="h-8 opacity-60"
						/>
						<p className="text-xs text-gray-500 dark:text-gray-400">
							Copyright Clarity Insights Group Ltd 2025
						</p>
						<button
							type="button"
							onClick={() => setShowTerms(true)}
							className="text-xs text-primary underline hover:opacity-80"
							aria-label="View Terms and Conditions"
							data-testid="terms-conditions-button"
						>
							View Terms & Conditions
						</button>
					</div>
				</div>
			</footer>
			{/* Global T&Cs Viewer for app pages */}
			<TermsDialog open={showTerms} onOpenChange={setShowTerms} mode="view" />
		</div>
	);
}
